<script lang="ts">
import type {
  BiomarkerType,
  OCGCurveType,
} from '@src/store/analysis/data-typing';

import type { ImageIndices } from '@/src/store/dicom-web/current-study-store';

import { computed, defineComponent, PropType, ref, watch } from 'vue';

import { colormaps } from '@/src/components/analysis/colormaps';
import { useAnalysisStore } from '@/src/store/analysis';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

export interface ColorbarInfo {
  colormap: string;
  minValue: number;
  maxValue: number;
  maskType?: string;
}

export interface ImageInfo {
  imageDataURL: string;
  title?: string;
  description?: string;
  tags?: string[];
  viewerID?: string;
  defaultTitle?: string;
  type?: 'image' | 'curves' | 'polarmap';
  curveType?: string;
  imageIndices?: ImageIndices;
  colorbarInfo?: ColorbarInfo;
}

export interface ScreenshotData {
  imageDataURL: string;
  title: string;
  description: string;
  tags: string[];
  colorbarInfo?: ColorbarInfo | null;
  imageIndices?: ImageIndices;
}

export default defineComponent({
  name: 'ScreenshotDialog',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    imageInfo: {
      type: Object as PropType<ImageInfo>,
      required: true,
    },
  },
  emits: {
    'update:dialogOpen': (value: boolean) => typeof value === 'boolean',
    'save': (data: ScreenshotData) => {
      return (
        typeof data === 'object'
        && 'imageDataURL' in data
        && 'title' in data
        && 'description' in data
        && 'tags' in data
      );
    },
    'discard': () => true,
  },
  setup(props, { emit }) {
    const viewportStateStore = useViewportStateStore();
    const currentStudyStore = useCurrentStudyStore();
    const analysisStore = useAnalysisStore();

    const viewID = computed(() => props.imageInfo?.viewerID);

    const curImageID = computed(() => {
      if (!viewID.value) return '';
      const indices = viewportStateStore.getSelectedImageIndices(viewID.value);
      if (!indices) return '';
      return currentStudyStore.getImageUID(indices);
    });

    const maskType = computed(() => {
      if (!viewID.value) return null;
      return viewportStateStore.getSelectedMaskByViewport(viewID.value);
    });

    const mask = computed(() => {
      if (!curImageID.value || !maskType.value) return null;
      const biomarkerContainer = analysisStore.getBiomarkerContainer();
      if (!biomarkerContainer) return null;
      return biomarkerContainer.getMaskByImageUIDAndType(
        curImageID.value,
        maskType.value as BiomarkerType,
      );
    });

    const currentColorbarInfo = computed<ColorbarInfo | null>(() => {
      if (props.imageInfo?.type === 'curves') {
        return null;
      }
      if (props.imageInfo?.colorbarInfo) {
        return props.imageInfo.colorbarInfo;
      }
      if (!maskType.value || !mask.value) {
        return null;
      }

      let min = 0;
      let max = 1;
      const biomarkerContainer = analysisStore.getBiomarkerContainer();
      const rangeInfo = biomarkerContainer.getBiomarkerNormalRange(
        maskType.value,
      );
      if (rangeInfo) {
        [min, max] = rangeInfo;
      }
      else {
        const range = mask.value.getRange();
        if (range !== undefined) {
          [min, max] = range;
        }
      }

      return {
        colormap: viewID.value
          ? viewportStateStore.getSelectedColormap(viewID.value)
          : 'plasma',
        minValue: min,
        maxValue: max,
        maskType: maskType.value,
      };
    });

    const getColormapGradientStyle = () => {
      if (!currentColorbarInfo.value) return {};
      const colormapName = currentColorbarInfo.value.colormap || 'plasma';
      const colormapFn = colormaps[colormapName];
      const gradientStops = Array.from({ length: 10 }, (_, i) => {
        const t = i / 9;
        return `${colormapFn(1 - t)} ${t * 100}%`;
      }).join(', ');

      return {
        background: `linear-gradient(to bottom, ${gradientStops})`,
      };
    };

    const localTitle = ref('');
    const localDescription = ref('');
    const localTags = ref<string[]>([]);

    const updateLocalFields = () => {
      const info = props.imageInfo || {};
      const sliceIdx = info.imageIndices?.sliceIdx;

      let title = info.title || info.defaultTitle || '';
      let description
        = info.description
          || (info.defaultTitle ? `Screenshot from ${info.defaultTitle}` : '');
      const tags = info.tags ? [...info.tags] : [];

      if (info.type === 'polarmap') {
        title = info.title || info.defaultTitle || 'Polarmap';
        description = info.description || `Screenshot displaying ${title}`;
      }
      else if (info.type === 'curves' && info.curveType) {
        const biomarkerContainer = analysisStore.getBiomarkerContainer();
        if (biomarkerContainer) {
          const label
            = biomarkerContainer.getLabelFromCurveType(
              info.curveType as OCGCurveType,
            ) || info.curveType;

          const formattedTitle = `${label}${
            sliceIdx !== undefined ? ` (Slice ${sliceIdx + 1})` : ''
          }`;
          title = formattedTitle;
          description = `Screenshot displaying ${label}`;
        }
      }
      else if (info.type !== 'curves' && mask.value && maskType.value) {
        const biomarkerContainer = analysisStore.getBiomarkerContainer();
        if (biomarkerContainer) {
          const label = biomarkerContainer.getLabelFromBiomarkerType(
            maskType.value as BiomarkerType,
          );
          const formattedTitle = `${label}${
            sliceIdx !== undefined ? ` (Slice ${sliceIdx + 1})` : ''
          }`;
          title = formattedTitle;
          description = `Screenshot displaying ${label}`;
        }
      }

      localTitle.value = title;
      localDescription.value = description;
      localTags.value = tags;
    };

    // Watch for imageInfo, open status, and mask resolution changes to reinitialize fields
    watch(
      () => [props.imageInfo, props.isOpen, mask.value, maskType.value],
      () => {
        if (props.isOpen) {
          updateLocalFields();
        }
      },
      { immediate: true, deep: true },
    );

    const handleSave = () => {
      const screenshotData: ScreenshotData = {
        imageDataURL: props.imageInfo.imageDataURL,
        title: localTitle.value || props.imageInfo.defaultTitle || '',
        description:
          localDescription.value
          || (props.imageInfo.defaultTitle
            ? `Screenshot from ${props.imageInfo.defaultTitle}`
            : ''),
        tags: localTags.value,
        colorbarInfo: currentColorbarInfo.value,
      };

      emit('save', screenshotData);
      emit('update:dialogOpen', false);
    };

    const handleDiscard = () => {
      emit('discard');
      emit('update:dialogOpen', false);
    };

    return {
      localTitle,
      localDescription,
      localTags,
      currentColorbarInfo,
      getColormapGradientStyle,
      handleSave,
      handleDiscard,
    };
  },
});
</script>

<template>
  <v-dialog
    :model-value="isOpen"
    max-width="600px"
    transition="dialog-bottom-transition"
    persistent
    @update:model-value="$emit('update:dialogOpen', $event)"
  >
    <v-card>
      <v-card-title>
        <span class="text-headline-medium">Screenshot Preview</span>
      </v-card-title>

      <v-card-text>
        <div class="d-flex flex-column gap-4">
          <!-- Screenshot Preview Wrapper for Colorbar Positioning -->
          <div class="screenshot-container">
            <v-img
              :src="imageInfo?.imageDataURL"
              max-width="100%"
              max-height="300px"
              class="screenshot-preview"
            />
            <!-- Colorbar overlay if mask is available -->
            <div v-if="currentColorbarInfo" class="floating-colorbar-preview">
              <div
                class="colorbar-gradient-preview"
                :style="getColormapGradientStyle()"
              />
              <div class="colorbar-labels-preview">
                <span class="colorbar-label-preview max-label-preview">{{
                  currentColorbarInfo.maxValue.toFixed(2)
                }}</span>
                <span class="colorbar-label-preview min-label-preview">{{
                  currentColorbarInfo.minValue.toFixed(2)
                }}</span>
              </div>
            </div>
          </div>

          <!-- Title Field -->
          <v-text-field
            v-model="localTitle"
            label="Title"
            variant="outlined"
            density="compact"
            :placeholder="imageInfo?.defaultTitle"
          />

          <!-- Description Field -->
          <v-textarea
            v-model="localDescription"
            label="Description (optional)"
            variant="outlined"
            density="compact"
            rows="3"
            :placeholder="
              imageInfo?.defaultTitle
                ? `Screenshot from ${imageInfo.defaultTitle}`
                : ''
            "
          />

          <!-- Tags Field -->
          <v-combobox
            v-model="localTags"
            label="Tags (optional)"
            variant="outlined"
            density="compact"
            multiple
            chips
            closable-chips
            hint="Press Enter to add tags"
            persistent-hint
          />
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />

        <v-btn color="secondary" variant="text" @click="handleDiscard">
          Discard
        </v-btn>

        <v-btn color="primary" variant="elevated" @click="handleSave">
          Save to Clipboard
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.screenshot-preview {
  border-radius: 4px;
}

.screenshot-container {
  position: relative;
  width: 100%;
}

.floating-colorbar-preview {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 12px;
  height: 120px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.1);
}

.colorbar-gradient-preview {
  width: 100%;
  height: 100%;
  border-radius: 3px;
}

.colorbar-labels-preview {
  position: absolute;
  left: 16px;
  top: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;
}

.colorbar-label-preview {
  color: white;
  font-size: 9px;
  font-family: sans-serif;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  white-space: nowrap;
}

.max-label-preview {
  margin-top: -4px;
}

.min-label-preview {
  margin-bottom: -4px;
}
</style>
